import { init, Sprite, GameLoop, Pool, Text, initPointer, track, load, imageAssets, getCanvas, Scene, pointerOver, audioAssets } from 'kontra';
import { Panel } from "../helpers/index";
import { Fruit } from './fruit';
import { levels } from "./levels";
import mangoImg from "../assets/images/mango.png";
import peachImg from "../assets/images/peach.png";
import cometSS from "../assets/images/SpaceBg/comet.png";
import stars1SS from "../assets/images/SpaceBg/stars1.png";
import stars2SS from "../assets/images/SpaceBg/stars2.png";
import barenPlanet from "../assets/images/Planets/Baren.png";
import icePlanet from "../assets/images/Planets/Ice.png";
import lavaPlanet from "../assets/images/Planets/Lava.png";
import terranPlanet from "../assets/images/Planets/Terran.png";
import badSound from "../assets/audio/bad.wav";
import goodSound from "../assets/audio/good.wav";
import beepSound from "../assets/audio/beep.wav";
import { Score } from '../interfaces/interfaces';
import { InitialMenu } from './initialMenu';
import { CongratsPanel } from "./congratsPanel";
import { SpaceItem } from './spaceItem';
import { BackgroundItems } from './BackgroundItems';
import { Planets } from './planets';

export class GameController {
  currentLevel: Level;
  ready: boolean = false;

  constructor() { }

  public startNewLevel(level) {
    let nextLevelNumber = (level + 1);
    if (level === 0) {
      nextLevelNumber = +(window.localStorage.getItem('next_starting_level') ?? 1)
    }

    this.currentLevel = new Level(level, nextLevelNumber, this);
  }

  gameStart() {
    this.ready = true;
    this.startNewLevel(0);
  }

  loadAssets() {
    return load(
      peachImg,
      mangoImg,
      cometSS,
      stars1SS,
      stars2SS,
      badSound,
      goodSound,
      beepSound,
      barenPlanet,
      lavaPlanet,
      terranPlanet,
      icePlanet
    ).then((assets) => {
      imageAssets.peach = assets[0];
      imageAssets.mango = assets[1];
      imageAssets.comet = assets[2];
      imageAssets.stars1 = assets[3];
      imageAssets.stars2 = assets[4];
      audioAssets.bad = assets[5];
      audioAssets.good = assets[6];
      audioAssets.beep = assets[7];
      imageAssets.baren = assets[8];
      imageAssets.lava = assets[9];
      imageAssets.terran = assets[10];
      imageAssets.ice = assets[11];

      audioAssets.bad.volume = 0.6;
      audioAssets.good.volume = 0.2;
      audioAssets.beep.volume = 0.5;
    })
  }

  update() {
    if (this.ready && this.currentLevel) {
      this.currentLevel.update();
    }
  }

  render() {
    if (this.ready && this.currentLevel) {
      this.currentLevel.render();
    }
  }
}

export class Level {
  private overlay: Sprite;
  private controller: GameController;
  private nextLevelNumber: number;
  public score: Score;
  private levelConfig;
  private displayLevel: Text;
  private mangoes: Fruit[];
  private peaches: Fruit[];
  private canvas;
  private impostorsText: Text;
  private initMenu: InitialMenu;
  private congratsPanel: CongratsPanel;
  private bkgItems: BackgroundItems;
  private isIntro: boolean;
  private planets: Planets;

  constructor(level: number, nextLevel: number, controller: GameController) {
    this.isIntro = level === 0;
    this.nextLevelNumber = nextLevel;
    this.controller = controller;

    this.score = {
      crewSize: 0,
      impostorsSize: 0,
      wronglyKilled: 0,
      impostorsFound: 0,
    }

    this.levelConfig = levels[level];
    this.canvas = getCanvas();
    this.initMenu = new InitialMenu(this);
    this.congratsPanel = new CongratsPanel(this);

    // Randomly position bkg
    this.canvas.style.backgroundPosition = `${Math.random() * 100}% ${Math.random() * 100}%, ${Math.random() * 100}% ${Math.random() * 100}%`;

    initPointer();

    this.peaches = (new Array(this.levelConfig.peaches.amount)).fill(null).map(_ => { return new Fruit(true, this.levelConfig.peaches.speed, this.score, this.afterPickCheck.bind(this)) })
    this.mangoes = (new Array(this.levelConfig.mangoes.amount)).fill(null).map(_ => { return new Fruit(false, this.levelConfig.mangoes.speed, this.score, this.afterPickCheck.bind(this)) })

    this.bkgItems = new BackgroundItems();
    this.overlay = Sprite({
      width: this.canvas.width,
      height: this.canvas.height,
      color: `rgba(${Math.floor(Math.random() * 50)},${Math.floor(Math.random() * 50)},${Math.floor(Math.random() * 50)},0.3)`
    })


    this.score.crewSize = this.mangoes.length;
    this.score.impostorsSize = this.peaches.length;
    this.score.wronglyKilled = 0;
    this.score.impostorsFound = 0;

    this.impostorsText = Text({
      text: 'You found',
      font: '24px future_narrow',
      color: '#aaa',
      x: this.canvas.width / 2,
      y: this.canvas.height - 50,
      anchor: { x: 0.5, y: 0.5 },
      textAlign: 'center'
    });

    this.planets = new Planets();

    this.displayLevel = Text({
      text: `Level ${level}/${levels.length - 1}`,
      x: 20,
      y: 22,
      anchor: { x: 0, y: 0.5 },
      color: '#ddd'
    });

    if (level === 0) {
      this.initMenu.show(this.nextLevelNumber);
      this.mangoes.forEach((mango) => mango.disablePicking())
      this.peaches.forEach((peach) => peach.disablePicking())
      this.displayLevel.text = '';
    }
  }

  public onButtonClick() {
    audioAssets.beep.play();
    this.nextLevelNumber = this.nextLevelNumber === levels.length ? 1 : this.nextLevelNumber;
    this.controller.startNewLevel(this.nextLevelNumber)
  }

  afterPickCheck(wasImpostor: boolean) {
    if (wasImpostor) {
      this.score.impostorsFound++;
      audioAssets.good.play();
    } else {
      this.score.wronglyKilled++;
      audioAssets.bad.play();
    }

    if (this.score.impostorsFound === this.score.impostorsSize) {
      if (this.nextLevelNumber === levels.length) {
        this.congratsPanel.show(1);
        window.localStorage.setItem('next_starting_level', 1 + '')
      } else {
        window.localStorage.setItem('next_starting_level', this.nextLevelNumber + '')
        this.initMenu.show(this.nextLevelNumber);
      }
      this.mangoes.forEach((mango) => mango.disablePicking())
      this.peaches.forEach((peach) => peach.disablePicking())
    }
  }

  update() {
    this.bkgItems.update()
    this.mangoes.forEach((mango) => mango.update())
    this.peaches.forEach((peach) => peach.update())
    this.initMenu.update();
    if (this.isIntro) {
      this.impostorsText.text = "";
    } else {
      this.impostorsText.text = `You found\n${this.score.impostorsFound} out of ${this.score.impostorsSize}\nimpostor${this.score.impostorsSize > 1 ? 's' : ''}.`
    }
  }

  render() {
    this.overlay.render();
    this.bkgItems.render();
    this.planets.render();
    this.impostorsText.render()
    this.displayLevel.render();
    this.mangoes.forEach((mango) => mango.render())
    this.peaches.forEach((peach) => peach.render())
    this.initMenu.render();
    this.congratsPanel.render();
  }
}

